import * as THREE from 'three';
import GPUCompute from './gpucompute';
import curlNoiseRender_vs from './curlNoiseRender.vs'
import curlNoiseRender_ps from './curlNoiseRender.ps'
import passThrough_vs from './passThrough.vs'
import curlNoiseCompute_ps from './curlNoiseCompute.ps'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'
import gsap from 'gsap'

export default class	Base
{
	constructor( canvas )
	{
		this.isReady = false;
		this.canvas = canvas;
    	this.scene = new THREE.Scene();
    	this.camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
    	this.renderer = new THREE.WebGLRenderer(
    		{
    			canvas:canvas,
    			preserveDrawingBuffer:false
    		});
    	this.renderer.autoClear = true;

    	this.clock = new THREE.Clock({autoStart:true});

    	this.resize();

    	// let ctrls = new OrbitControls(this.camera, this.renderer.domElement);

    	const texLoader = new THREE.TextureLoader().load('dust.png', ( t ) =>
    		{
    			this.init();
    			this.renderShader.uniforms.albedoTex.value = t;
    		});
	}

	resize()
	{
		this.canvas.width = window.innerWidth
		this.canvas.height = window.innerHeight

		this.camera.aspect = window.innerWidth / window.innerHeight;
		this.camera.updateProjectionMatrix();

		this.renderer.setSize(window.innerWidth, window.innerHeight*1.0);
	}

	init()
	{
		this.camera.position.set(0, 50, 0);
		this.camera.lookAt(new THREE.Vector3(0, 0, 0));

		//-------------------------------------------------------------------------
		this.particlesCount = 1024;

		//-------------------------------------------------------------------------
		// create render shader
		this.renderShader = this.generateRenderShader();

		//-------------------------------------------------------------------------
		// create compute shader
		this.computeShader = this.generateComputeShader();

		//-------------------------------------------------------------------------
		// create particles geom
		var geom = new THREE.BufferGeometry();
		var positions = new Float32Array(this.particlesCount * this.particlesCount * 3);
		var uvs = new Float32Array(this.particlesCount * this.particlesCount * 2);

		this.generateUVs(uvs);

		geom.setAttribute("position", new THREE.BufferAttribute(positions, 3));
		geom.setAttribute("idx", new THREE.BufferAttribute(uvs, 2));

		this.particlesMesh = new THREE.Points(geom, this.renderShader);
		this.scene.add(this.particlesMesh);

		this.gpuParticles = new GPUCompute(this.renderer, this.particlesCount, this.computeShader);

		let span = 100;
		let count = this.particlesCount * this.particlesCount * 4;
		let data = new Float32Array(count);
		for (let i = 0; i < count; i += 4)
		{
			data[i + 0] = Math.random() * span - (span * 0.5);
			data[i + 1] = Math.random() * span - (span * 0.5);
			data[i + 2] = Math.random() * span - (span * 0.5);
			data[i + 3] = (0.2 + Math.random() * .8);
		}
		this.gpuParticles.addData(data, "positionTex");

		for (let i = 0; i < count; i += 4)
		{
			data[i + 0] = (0.2 + Math.random() * .8) * 0.032;
			data[i + 1] = data[i + 3];
			data[i + 2] = Math.random();
			data[i + 3] = Math.random();
		}
		this.gpuParticles.addData(data, "velocityTex");

		document.body.addEventListener("click", this.onClick.bind(this));

		this.isReady = true;
	}

	onClick()
	{
		let uniforms = this.computeShader.uniforms;
		gsap.killTweensOf(uniforms.uSpeed.value);

		uniforms.uOctaves.value = (uniforms.uOctaves.value + 1) % 8;
		uniforms.uSpeed.value = 100;
		uniforms.uDirX.value = Math.random() > 0.5 ? 1 : -1;
		uniforms.uDirY.value = Math.random() > 0.5 ? 1 : -1;
		uniforms.uFbmShift.value = Math.random() * 256 - 128;
		uniforms.uTime.value /= 10;

		gsap.to(uniforms.uDiv, {value:Math.random() * Math.cos(uniforms.uTime.value) * 0.1, duration:2, delay:.16});
		gsap.to(uniforms.uSpeed, {value:8, duration:.32});
	}

	generateUVs( uvs )
	{
		var col = 0;
		var row = 0;
		var res = this.particlesCount * this.particlesCount;
		var index = 0;

		for (var i = 1; i <= res; i += 1)
		{
			uvs[index++] = col / (this.particlesCount - 1);
			uvs[index++] = row / (this.particlesCount - 1);
			col += 2;

			if (i % this.particlesCount == 0 && i)
			{
				row++;
				col = 0;
			}
		}
	};

	generateRenderShader()
	{
		return new THREE.ShaderMaterial(
		{
			uniforms:
			{
				resolution:{type:"v2", value:new THREE.Vector2(this.particlesCount, this.particlesCount)},
				positionTex:{type:"t", value:null},
				velocityTex:{type:"t", value:null},
				albedoTex:{type:"t", value:null},
				clr:{type:"c", value:new THREE.Color(0xFFFFFF)},
				size:{type:"f", value:1.0},
			},
			vertexShader:curlNoiseRender_vs,
			fragmentShader:curlNoiseRender_ps,
			blending:THREE.AdditiveBlending,
			depthTest:false,
			depthWrite:false,
			transparent:true
		});
	};

	generateComputeShader()
	{
		return new THREE.ShaderMaterial(
		{
			uniforms:
			{
				resolution:{type:"v2", value:new THREE.Vector2(this.particlesCount, this.particlesCount)},
				positionTex:{type:"t", value:null},
				velocityTex:{type:"t", value:null},
				uTime:{type:"f", value:0.0},
				uDiv:{type:"f", value:0.018},
				uSpeed:{type:"f", value:8.0},
				uOctaves:{type:"i", value:3},
				uDirX:{type:"f", value:1.0},
				uDirY:{type:"f", value:1.0},
				uFbmShift:{type:"f", value:128.0},
			},
			vertexShader:passThrough_vs,
			fragmentShader:curlNoiseCompute_ps
		});
	};

	update()
	{
		if (!this.isReady)
			return;

		this.particlesMesh.rotation.x += .05 * this.clock.getDelta();

		this.gpuParticles.update();
		this.renderShader.uniforms.positionTex.value = this.computeShader.uniforms.positionTex.value;
		this.renderShader.uniforms.velocityTex.value = this.computeShader.uniforms.velocityTex.value;
		this.computeShader.uniforms.uTime.value += this.clock.getDelta();


		this.renderer.render(this.scene, this.camera);
	};
}


// WEBPACK FOOTER //
// ./src/base.js